import React, { JSX } from 'react';
import { Box, Heading, VStack, Text, Container, SimpleGrid, Flex, Icon, HStack } from '@chakra-ui/react';
import { Cloud, HardDrive, Smartphone, Globe, Shield, Zap } from 'lucide-react';

interface PropsInterface {
	children?: React.ReactNode;
}

function CloudStorageSection(_props: PropsInterface): JSX.Element {
	return (
		<Box py={32} bg="white" position="relative" overflow="hidden">
			{/* Background decorative icons */}
			<Icon as={Cloud} position="absolute" top="10%" left="5%" boxSize={24} color="blue.100" opacity={0.8} />
			<Icon
				as={HardDrive}
				position="absolute"
				top="20%"
				right="10%"
				boxSize={20}
				color="purple.100"
				opacity={0.25}
			/>
			<Icon as={Globe} position="absolute" bottom="15%" left="8%" boxSize={28} color="green.100" opacity={0.7} />
			<Icon
				as={Smartphone}
				position="absolute"
				bottom="25%"
				right="5%"
				boxSize={16}
				color="blue.100"
				opacity={0.3}
			/>
			<Icon as={Shield} position="absolute" top="50%" left="3%" boxSize={18} color="gray.100" opacity={0.9} />
			<Icon as={Zap} position="absolute" top="40%" right="3%" boxSize={22} color="yellow.100" opacity={0.9} />

			<Container maxW="6xl" position="relative" zIndex={1}>
				<VStack textAlign="center" gap={0} mb={16}>
					<Text
						fontSize="11px"
						fontWeight="500"
						textTransform="uppercase"
						letterSpacing="0.22em"
						color="gray.400"
					>
						Cloud Storage
					</Text>
					<Heading
						mt={5}
						mb={8}
						maxW="2xl"
						fontSize={{ base: '2.25rem', md: '3.75rem' }}
						fontWeight={400}
						lineHeight={1}
						letterSpacing="-0.035em"
					>
						Your Creative Workspace, Everywhere
					</Heading>
					<Text maxW="2xl" fontSize={{ base: '1.125rem', md: '1.25rem' }} color="gray.600" lineHeight={1.6}>
						10GB secure storage. Access your projects from any device, anytime.
					</Text>
				</VStack>

				<SimpleGrid columns={{ base: 1, md: 3 }} gap={8}>
					<Box
						p={8}
						shadow="md"
						rounded="2xl"
						border="1px solid"
						borderColor="gray.100"
						transition="all 0.3s"
						_hover={{ transform: 'translateY(-2px)', shadow: 'md', bg: 'realWhite/50' }}
						h="full"
					>
						<Flex
							w={12}
							h={12}
							bgGradient="to-br"
							gradientFrom="primary"
							gradientTo="secondary"
							rounded="xl"
							align="center"
							justify="center"
							mb={4}
						>
							<Icon as={HardDrive} boxSize={6} color="white" />
						</Flex>
						<Heading size="md" mb={3} color="gray.900">
							10GB Storage
						</Heading>
						<Text color="gray.600" fontSize="sm" lineHeight={1.6}>
							Storage for all your images, videos, and creative assets.
						</Text>
					</Box>

					<Box
						p={8}
						shadow="md"
						rounded="2xl"
						border="1px solid"
						borderColor="gray.100"
						transition="all 0.3s"
						_hover={{ transform: 'translateY(-2px)', shadow: 'md', bg: 'realWhite/50' }}
						h="full"
					>
						<Flex
							w={12}
							h={12}
							bgGradient="to-br"
							gradientFrom="primary"
							gradientTo="secondary"
							rounded="xl"
							align="center"
							justify="center"
							mb={4}
						>
							<Icon as={Globe} boxSize={6} color="white" />
						</Flex>
						<Heading size="md" mb={3} color="gray.900">
							Access Anywhere
						</Heading>
						<Text color="gray.600" fontSize="sm" lineHeight={1.6}>
							Work from your browser or our mobile app. Your entire workspace stays with you.
						</Text>
					</Box>

					<Box
						p={8}
						shadow="md"
						rounded="2xl"
						border="1px solid"
						borderColor="gray.100"
						transition="all 0.3s"
						_hover={{ transform: 'translateY(-2px)', shadow: 'md', bg: 'realWhite/50' }}
						h="full"
					>
						<Flex
							w={12}
							h={12}
							bgGradient="to-br"
							gradientFrom="primary"
							gradientTo="secondary"
							rounded="xl"
							align="center"
							justify="center"
							mb={4}
						>
							<Icon as={Cloud} boxSize={6} color="white" />
						</Flex>
						<Heading size="md" mb={3} color="gray.900">
							Secure & Synced
						</Heading>
						<Text color="gray.600" fontSize="sm" lineHeight={1.6}>
							Automatic backup and real-time sync across all your devices.
						</Text>
					</Box>
				</SimpleGrid>

				<HStack justify="center" mt={12} gap={8} flexWrap="wrap">
					<Flex align="center" gap={2}>
						<Icon as={Smartphone} boxSize={5} color="gray.400" />
						<Text color="gray.500" fontSize="sm" fontWeight="500">
							Mobile App
						</Text>
					</Flex>
					<Flex align="center" gap={2}>
						<Icon as={Shield} boxSize={5} color="gray.400" />
						<Text color="gray.500" fontSize="sm" fontWeight="500">
							Encrypted Storage
						</Text>
					</Flex>
					<Flex align="center" gap={2}>
						<Icon as={Zap} boxSize={5} color="gray.400" />
						<Text color="gray.500" fontSize="sm" fontWeight="500">
							Fast Access
						</Text>
					</Flex>
				</HStack>
			</Container>
		</Box>
	);
}

export default CloudStorageSection;
